import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  MenuItem,
  Checkbox,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { machineService, pressFormService } from '../services/api';
import EntityImportExportButtons from '../components/EntityImportExportButtons';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Понеділок' },
  { value: 1, label: 'Вівторок' },
  { value: 2, label: 'Середа' },
  { value: 3, label: 'Четвер' },
  { value: 4, label: 'П\'ятниця' },
  { value: 5, label: 'Субота' },
  { value: 6, label: 'Неділя' },
];

const defaultWorkSchedules = () =>
  DAYS_OF_WEEK.map((d) => ({
    day_of_week: d.value,
    start_time: '08:00',
    end_time: '17:00',
    is_active: d.value < 5,
  }));

const cmpNaturalUk = (a, b) =>
  String(a ?? '').localeCompare(String(b ?? ''), 'uk', { numeric: true, sensitivity: 'base' });

function MachinesPage() {
  const [machines, setMachines] = useState([]);
  const [pressForms, setPressForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    identifier: '',
    compatible_press_forms: [],
    is_active: true,
    description: '',
    work_schedules: defaultWorkSchedules(),
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [machinesRes, pressFormsRes] = await Promise.all([
        machineService.getAll(),
        pressFormService.getAll(),
      ]);
      const machineList = Array.isArray(machinesRes.data.results)
        ? machinesRes.data.results
        : Array.isArray(machinesRes.data)
          ? machinesRes.data
          : [];
      machineList.sort((a, b) => {
        const c = cmpNaturalUk(a.identifier, b.identifier);
        return c !== 0 ? c : cmpNaturalUk(a.name, b.name);
      });
      setMachines(machineList);
      const pfList = Array.isArray(pressFormsRes.data.results)
        ? pressFormsRes.data.results
        : Array.isArray(pressFormsRes.data)
          ? pressFormsRes.data
          : [];
      pfList.sort((a, b) => cmpNaturalUk(a.identifier, b.identifier) || cmpNaturalUk(a.name, b.name));
      setPressForms(pfList);
    } catch (error) {
      console.error('Помилка завантаження:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (machine = null) => {
    if (machine) {
      setEditing(machine.id);
      const schedules = (machine.work_schedules || []).length
        ? DAYS_OF_WEEK.map((d) => {
            const existing = (machine.work_schedules || []).find(
              (ws) => ws.day_of_week === d.value
            );
            return existing
              ? {
                  day_of_week: d.value,
                  start_time: existing.start_time?.slice(0, 5) || '08:00',
                  end_time: existing.end_time?.slice(0, 5) || '17:00',
                  is_active: existing.is_active !== false,
                }
              : {
                  day_of_week: d.value,
                  start_time: '08:00',
                  end_time: '17:00',
                  is_active: false,
                };
          })
        : defaultWorkSchedules();
      setFormData({
        name: machine.name,
        identifier: machine.identifier,
        compatible_press_forms: machine.compatible_press_forms || [],
        is_active: machine.is_active,
        description: machine.description || '',
        work_schedules: schedules,
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        identifier: '',
        compatible_press_forms: [],
        is_active: true,
        description: '',
        work_schedules: defaultWorkSchedules(),
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name,
        identifier: formData.identifier,
        compatible_press_forms: formData.compatible_press_forms,
        is_active: formData.is_active,
        description: formData.description,
        work_schedules: formData.work_schedules
          .filter((ws) => ws.is_active)
          .map((ws) => ({
            day_of_week: ws.day_of_week,
            start_time: ws.start_time,
            end_time: ws.end_time,
            is_active: true,
          })),
      };
      if (editing) {
        await machineService.update(editing, payload);
      } else {
        await machineService.create(payload);
      }
      handleClose();
      loadData();
    } catch (error) {
      console.error('Помилка збереження:', error);
      const msg =
        error.response?.data?.detail ||
        (typeof error.response?.data === 'object' && error.response?.data !== null
          ? Object.entries(error.response.data)
              .map(([k, v]) => (Array.isArray(v) ? v.join(' ') : v))
              .join(' ')
          : null) ||
        error.message ||
        'Помилка збереження даних';
      alert(msg);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Ви впевнені, що хочете видалити цей станок?')) {
      try {
        await machineService.delete(id);
        loadData();
      } catch (error) {
        console.error('Помилка видалення:', error);
        alert('Помилка видалення');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4">Станки</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <EntityImportExportButtons entityKey="machines" label="ТПА" />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Додати станок
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Ідентифікатор</TableCell>
              <TableCell>Назва</TableCell>
              <TableCell>Сумісні прес-форми</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {machines.map((machine) => (
              <TableRow key={machine.id}>
                <TableCell>{machine.identifier}</TableCell>
                <TableCell>{machine.name}</TableCell>
                <TableCell>
                  {machine.compatible_press_forms_data?.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {machine.compatible_press_forms_data.slice(0, 3).map((pf) => (
                        <Chip key={pf.id} label={pf.identifier} size="small" />
                      ))}
                      {machine.compatible_press_forms_data.length > 3 && (
                        <Chip label={`+${machine.compatible_press_forms_data.length - 3}`} size="small" />
                      )}
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={machine.is_active ? 'Активна' : 'Неактивна'}
                    color={machine.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(machine)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(machine.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editing ? 'Редагувати станок' : 'Додати станок'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Назва"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Ідентифікатор"
              value={formData.identifier}
              onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
              fullWidth
            />
            <TextField
              select
              SelectProps={{
                multiple: true,
              }}
              label="Сумісні прес-форми"
              value={formData.compatible_press_forms}
              onChange={(e) => setFormData({ ...formData, compatible_press_forms: e.target.value.filter(v => v !== '__all__') })}
              fullWidth
            >
              <MenuItem
                value="__all__"
                onClick={(e) => {
                  e.stopPropagation();
                  const all = pressForms.map(pf => pf.id);
                  setFormData({ ...formData, compatible_press_forms: formData.compatible_press_forms.length === pressForms.length ? [] : all });
                }}
              >
                <Checkbox
                  checked={formData.compatible_press_forms.length === pressForms.length && pressForms.length > 0}
                  indeterminate={formData.compatible_press_forms.length > 0 && formData.compatible_press_forms.length < pressForms.length}
                />
                <em>Обрати всі</em>
              </MenuItem>
              {pressForms.map((pf) => (
                <MenuItem key={pf.id} value={pf.id}>
                  {pf.identifier} - {pf.name}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Активна"
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
              Тижневий робочий графік
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>День</TableCell>
                    <TableCell>Час з</TableCell>
                    <TableCell>Час по</TableCell>
                    <TableCell>Працює</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.work_schedules.map((ws, index) => (
                    <TableRow key={ws.day_of_week}>
                      <TableCell>
                        {DAYS_OF_WEEK.find((d) => d.value === ws.day_of_week)?.label}
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="time"
                          size="small"
                          value={ws.start_time}
                          onChange={(e) => {
                            const next = [...formData.work_schedules];
                            next[index] = { ...next[index], start_time: e.target.value };
                            setFormData({ ...formData, work_schedules: next });
                          }}
                          inputProps={{ step: 300 }}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="time"
                          size="small"
                          value={ws.end_time}
                          onChange={(e) => {
                            const next = [...formData.work_schedules];
                            next[index] = { ...next[index], end_time: e.target.value };
                            setFormData({ ...formData, work_schedules: next });
                          }}
                          inputProps={{ step: 300 }}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={ws.is_active}
                          onChange={(e) => {
                            const next = [...formData.work_schedules];
                            next[index] = { ...next[index], is_active: e.target.checked };
                            setFormData({ ...formData, work_schedules: next });
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TextField
              label="Опис"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MachinesPage;
